/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import React, { useContext, useEffect, useState } from 'react';
import '../SidePanel.css';
import '../CommonStyle.css';
import Mesh from '../../../icons/Mesh.svg?react';
import Model3D from '../../../icons/Model3D.svg?react';
import Calendar from '../../../icons/Calendar.svg?react';
import FormControl from '@mui/material/FormControl';
import { GlobalContext } from '../../../store/Store';
import ActionType from '../../../store/ActionType';
import { ModelType } from '../../../Models/Mesh';
import ProviderDialog from '../../ProviderDialog';
import ProviderIcon from '../../ProviderIcon';
import SelectModelArea from './SelectModelArea';
import Skeleton from '@mui/material/Skeleton';
import { CardHeader, Card, Typography } from '@mui/material';
import { MeshFiles } from '../../../apis/meshApi';
import MeshListDialog from '../../MeshListDialog';
import { IGlobalContext } from '../../app/AppWrapper';
import { getMeshBoundaries, deleteMeshFiles, getExtractionBuilder } from '../../app/apiFunctions';
import { cleanUpMeshTiles, cleanUpMeshBoundaries } from '../../map/MapFunctions';
import OlView from 'ol/View';
// import DatePicker from '../../DatePicker/DatePicker';
import { Provider } from '../../../generated/src/models';
import TemporalDataCoverage from '../TemporalDataCoverage/TemporalDataCoverage';
import mikeSharedTheme from '../../../styles/mikeSharedTheme';
import { StyledSelect } from '../StyledSelect';
import MikeDatePicker from '../../mike-date-picker';
import { customDateTimeFormat } from '../../utils';

const selectModelStyle =  css`
  width: ${mikeSharedTheme.spacing(35)}
`

const selectPeriodContainerStyle =  css`
  padding-left: 11px; 
  padding-right: 11px; 
`
const placeHolder = css`   
  width: 51px;
`
const flexStyle = css`
  display: flex;  
`

const titleStyle = css`
  display: flex;  
   width: 280px;
`

const flexCenterStyle = css`
  display: flex;    
  align-items: center;
`

const datePickerContainerStyle = css`
  width: 280px;
    padding-left: 11px; 
  padding-right: 11px; 
  padding-bottom: 11px; 
`

const dateStartLabelStyle = css`
  width: 50vw;
`
const dateEndLabelStyle =  css`
  width: 50vw;
  padding-left: ${mikeSharedTheme.spacing(1)}; 
`

function ExtractionSettingsPanel() {
  // global store
  const [globalState, globalDispatch] = useContext(GlobalContext);
  const globalContext: IGlobalContext = { state: globalState, dispatch: globalDispatch };
  const providers =
    globalState.data.extractionbuilderdata && globalState.data.extractionbuilderdata.providers
      ? globalState.data.extractionbuilderdata.providers
      : [];

  const [provider, setProvider] = useState<Provider | undefined>();
  const openDialog = (value: Provider) => () => {
    setProvider(value);
  };
  const closeDialog = () => setProvider(undefined);

  const [showMeshFilesDialog, setShowMeshFilesDialog] = useState(false);
  const [meshesToDelete, setMeshesToDelete] = useState<MeshFiles>([]);

  useEffect(() => {
    // When logic is in place for the backend, this code should be move to the try/catch endpoint method So the UI don't update if the opeation goes wrong:
    if (meshesToDelete && meshesToDelete.length > 0) {
      //Filter out the choosen mesh files for deletion:
      const meshFilesLeft = globalState.meshFilesImported.filter(el => {
        return !meshesToDelete.some(f => {
          return f === el.meshName;
        });
      });

      // Delete the files endpoint:
      deleteMeshFiles({
        projectID: globalState.project.currentProject.id,
        meshFiles: meshesToDelete,
        globalContext,
      });

      // If meshfile is the selected and loaded do something:
      meshesToDelete.map(item => {
        if (item === globalState.mesh.meshName) {
          if (meshFilesLeft.length > 0) {
            //Load the first mesh file in the list
            getMeshBoundaries({
              meshName: meshFilesLeft[0].meshName,
              globalContext,
            });
          } else {
            //Clear everything
            globalDispatch({ type: ActionType.SET_DATA_EXTRACTION_BUILDER, data: null });
            globalDispatch({ type: ActionType.SET_MESH_NAME, data: '' });
            globalDispatch({ type: ActionType.SET_MESH_FEATURES, data: null });
            globalDispatch({ type: ActionType.SET_MESH_PROJECTION, data: null });
            globalDispatch({ type: ActionType.SET_MESH_TILEGRID_CONFIG, data: null });
            cleanUpMeshTiles(globalState);
            cleanUpMeshBoundaries(globalState);
            // Reset Map view
            (globalContext.state.map as any).olMap.setView(
              new OlView({
                center: [0, 0],
                zoom: 3,
                projection: 'EPSG:3857',
              })
            );
          }
        }
      });

      //Push the Mesh file list to the store
      globalDispatch({
        type: ActionType.SET_MESH_FILES_IMPORTED,
        data: meshFilesLeft,
      });
    }
  }, [meshesToDelete]);

  const onDeleteMeshClick = () => {
    setShowMeshFilesDialog(true);
  };

  const onChangeStartDate = (e: Date) => {  
    const isoString = customDateTimeFormat(e)
    globalDispatch({
        type: ActionType.SET_EXTRACTION_STARTDATE,
        data: isoString,
      });
  };
  const onChangeEndDate = (e: Date) => {
    const isoString = customDateTimeFormat(e)
     globalDispatch({
        type: ActionType.SET_EXTRACTION_ENDDATE,
        data: isoString,
      });
  };  

  const SkeletonItem: React.FC = () => {
    const skeletonRows = [1, 2, 3, 4, 5, 6, 7];
    return (
      <div style={{ overflowY: 'hidden' }}>
        {skeletonRows.map((ind: number) => (
          <Card key={ind}>
            <CardHeader
              avatar={<Skeleton animation="wave" variant="circular" width={32} height={32} />}
              title={
                <Skeleton animation="wave" height={12} width="80%" style={{ marginBottom: 6 }} />
              }
              subheader={
                <Skeleton animation="wave" height={12} width="40%" style={{ marginBottom: 6 }} />
              }></CardHeader>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <div className="noHorizontalScroll">
      <div className="panelItem">
        <div className="panelItemIcon">
          <Mesh />
        </div>
        <div className="panelItemContent">
          <FormControl fullWidth>
            {/* <InputLabel htmlFor="mesh-selector">Select model area</InputLabel> */}
            <SelectModelArea />
          </FormControl>
          {globalState &&
          globalState.meshFilesImported &&
          globalState.meshFilesImported.length > 0 ? (
            <div onClick={onDeleteMeshClick} className="deleteMeshFilesLaunch">
              Delete mesh files
            </div>
          ) : (
            ''
          )}
        </div>
      </div>

      <div className="panelItem">
        <div className="panelItemIcon">
          <Model3D />
        </div>
        <div css={selectModelStyle}>
          <StyledSelect 
            label={"Select model type"}            
            fullWidth={true}
            name={globalState.mesh.modelType}
            value={globalState.mesh.modelType}         
            valueField={"modelType"}
            primaryField={"modelType"}
            items={[
              {modelType: ModelType.MIKE21},
              {modelType: ModelType.MIKE3},
              {modelType: ModelType.MIKE21SW}
            ]}
            onChange={e => {
              if (
                e.target.value === 'MIKE 21 SW' ||
                globalState.mesh.modelType === 'MIKE 21 SW'
              ) {
                getExtractionBuilder({
                  meshName: globalState.mesh.meshName,
                  projectID: globalState.project.currentProject.projectGuid,                  
                  modelType: (e.target.value as any).replace(/\s+/g, ''),
                  globalDispatch,
                });
              }
              globalDispatch({ type: ActionType.SET_MESH_MODEL_TYPE, data: e.target.value });
            }}
          />  
        </div>
      </div>
   
   {/*<div className="panelItem">
      <div className="panelItemIcon">
        <Calendar />
      </div>
      <div className="panelItemContent contentTable">
        <DatePicker fromDate="1970-01-01" toDate="2030-12-31" />
      </div>  */} 
      <div css={selectPeriodContainerStyle}>        
        <div css={flexStyle}>  
          <div css={placeHolder}/>
          <div css={titleStyle}>
            <Typography css={dateStartLabelStyle}  variant="body2">{'Start Date'}</Typography>
            <Typography css={dateEndLabelStyle} variant="body2">{'End Date'}</Typography>    
          </div>        
        </div>
        <div css={flexCenterStyle}>
          <Calendar />
          <div css={datePickerContainerStyle}>            
          <MikeDatePicker
            rangePicker
            minDateStart={new Date("1970-01-01")}
            maxDateStart={new Date("2030-12-31")}
            minDateEnd={new Date("1970-01-01")}
            maxDateEnd={new Date("2030-12-31")}
            defaultStartDate={new Date(globalState.mesh.extractionStartDate)}
            onChangeStartDate={onChangeStartDate}
            defaultEndDate={new Date(globalState.mesh.extractionEndDate)}
            onChangeEndDate={onChangeEndDate}
            hideIcon={true}                             
          />
          </div> 
        </div> 
      </div>  

      <div className="panelItemHeader">Data sources and temporal availability</div>

      <div className="dataSources scrollableContent">
        {globalState.data && globalState.data.fetchingExtractionBuilderData ? (
          <SkeletonItem />
        ) : (
          providers &&
          providers.map((prov, i) => (
            <div className="panelItem itemBorder noPadding" key={i}>
              <div className="panelItemIcon">
                <ProviderIcon onClick={openDialog(prov)} provider={prov} />
              </div>
              <div className="panelItemContentTitle panelItemContent">
                <div onClick={openDialog(prov)} className="cursorPointer">
                  <Typography variant="body2">{prov.displayName}</Typography>
                </div>
                <TemporalDataCoverage provider={prov} />
              </div>
            </div>
          ))
        )}
      </div>

      {providers && <ProviderDialog open={!!provider} provider={provider} onClose={closeDialog} />}

      {showMeshFilesDialog && (
        <MeshListDialog
          open={showMeshFilesDialog}
          onClose={() => {
            setShowMeshFilesDialog(false);
          }}
          onSubmit={sel => setMeshesToDelete(sel)}
        />
      )}
    </div>
  );
}

export default React.memo(ExtractionSettingsPanel);
